import {
	createContext,
	type PropsWithChildren,
	useContext,
	useMemo,
} from 'react';
import type { Nullable } from '@gov-nx/core/types';
import type { PPPUkonceniContextTypes, ColumsType } from './service.types';
import { usePPPDetailQuery } from '../detail/hooks/usePPPDetailQuery';
import { usePPPLocale } from '../usePPPLocale';
import { toDateReadable } from '@gov-nx/utils/common';
import { useNavigate } from 'react-router-dom';
import { serviceSubRouteResolver } from '@gov-nx/web';
import { usePPPTerminate } from './hooks/terminate/usePPPTerminate';
import { useServiceAuthorization } from '@gov-nx/core/hooks';
import { useAutorizaceContext } from 'libs/component/web/src/Autorizace/Context';
import { useFormBuilderWithConsent } from '@gov-nx/core/service';

type TerminateFormData = { __souhlas?: boolean };

export const PPPUkonceniContext =
	createContext<Nullable<PPPUkonceniContextTypes>>(null);

const AUTHORIZATION_SERVICE_NAME = 'PODANI_PPP_UKONCENI';

export const PPPUkonceniContextProvider = ({
	children,
	pppId,
}: PropsWithChildren<{ pppId: string }>) => {
	const navigate = useNavigate();
	const { t } = usePPPLocale();
	const query = usePPPDetailQuery({ pppId });
	const authContext = useAutorizaceContext();

	const { isLowAuthorization, isAuthorizationEnforced } =
		useServiceAuthorization(AUTHORIZATION_SERVICE_NAME);

	const pppTerminate = usePPPTerminate({
		pppId,
		serviceName: AUTHORIZATION_SERVICE_NAME,
		authContext,
	});

	const formDefinition = useFormBuilderWithConsent<
		TerminateFormData,
		Record<string, never>
	>([], { isLowAuthorization, isDisabled: pppTerminate.isTerminating });

	const onTerminate = formDefinition.formMethods.handleSubmit(() =>
		pppTerminate.terminate()
	);

	const columns = useMemo<ColumsType>(
		() => [
			{
				id: 'nazev',
				label: t('ukonceni.table.nazev'),
				valueFormatter: ({ value }) => value ?? '-',
			},
			{
				id: 'datumPodani',
				label: t('ukonceni.table.datum-podani'),
				valueFormatter: ({ data }) =>
					data?.datumPodani ? toDateReadable(data.datumPodani) : '-',
			},
			{
				id: 'idZadosti',
				label: t('ukonceni.table.id-zadosti'),
				valueFormatter: ({ value }) => value ?? '-',
			},
		],
		[t]
	);

	return (
		<PPPUkonceniContext.Provider
			value={{
				columns,
				data: query.data,
				isLoading: query.isLoading,
				isTerminating: pppTerminate.isTerminating,
				isLowAuthorization,
				isAuthorizationEnforced,
				formDefinition,
				onTerminate,
				onBackToDetail: () =>
					navigate(
						serviceSubRouteResolver('PPP', 'detail', { pppId }, ['pppId'])
					),
			}}>
			{children}
		</PPPUkonceniContext.Provider>
	);
};

export const usePPPUkonceniContext = (): PPPUkonceniContextTypes =>
	useContext(PPPUkonceniContext) as PPPUkonceniContextTypes;
